import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@artstorefronts/ui';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  ImageIcon,
  SendIcon,
  Loader2,
  ZoomInIcon,
  Shield,
  AlertCircle,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { useQuery, gql } from '@apollo/client';

import { useAuth } from '~/context/auth';
import { useImages } from '~/context/image';
import { useChatUI } from '~/context/chatUI';
import ArtworkDetailsForm from '~/components/ArtworkDetailsForm';
import { Mocks } from '~/components/Mocks';
import ArtGuardModal from '~/components/ArtGuardModal';
import ShareModal from '~/components/ShareModal';
import { useAnalytics } from '~/context/analytics';
import { Completion } from '~/types';
import { getPublicImageUrl } from '../utils';
import { useTranslation } from 'react-i18next';
import { IMAGE_QUERY } from '~/graphql/queries';

const LATEST_COPYRIGHT_SCAN = gql`
  query LatestCopyrightScan($imageId: ID!) {
    latestCopyrightScan(imageId: $imageId) {
      id
      status
      results
      createdAt
    }
  }
`;

// Helper function to detect if an image URL is a user image
function detectUserImage(
  src: string,
  currentUserId?: number,
  attachmentImageId?: string
): { isUserImage: boolean; detectedImageId?: string } {
  // If we have an imageId from the attachment object, use it directly
  if (attachmentImageId != null && attachmentImageId !== '') {
    return { isUserImage: true, detectedImageId: String(attachmentImageId) };
  }

  // Check for optimized URL pattern: https://domain.com/optimized/imageId
  const optimizedUrlPattern = /\/optimized\/(\d+)/;
  const match = src.match(optimizedUrlPattern);

  if (match) {
    return { isUserImage: true, detectedImageId: match[1] };
  }

  // Check for thumbnail URL pattern: https://domain.com/thumbnail/imageId
  const thumbnailUrlPattern = /\/thumbnail\/(\d+)/;
  const thumbnailMatch = src.match(thumbnailUrlPattern);

  if (thumbnailMatch) {
    return { isUserImage: true, detectedImageId: thumbnailMatch[1] };
  }

  // Check for attachment URL pattern: https://domain.com/<user-id>/imageId
  const attachmentUrlPattern = /\/(\d+)\/(\d+)/;
  const attachmentMatch = src.match(attachmentUrlPattern);
  if (attachmentMatch) {
    const urlUserId = parseInt(attachmentMatch[1]);
    const imageId = attachmentMatch[2];

    // Only consider it a user image if the user ID in the URL matches the current user's ID
    if (currentUserId && urlUserId === currentUserId) {
      return { isUserImage: true, detectedImageId: imageId };
    }
  }

  return { isUserImage: false };
}

// Helper function to get ArtGuard icon based on scan status
function getArtGuardIcon(scanData: any) {
  const scan = scanData?.latestCopyrightScan;

  if (!scan) {
    return { icon: Shield, className: 'w-3 h-3 mr-1.5' };
  }

  if (scan.status === 'scheduled') {
    return { icon: Clock, className: 'w-3 h-3 mr-1.5 text-gray-500' };
  }

  if (scan.status !== 'completed') {
    return { icon: Shield, className: 'w-3 h-3 mr-1.5' };
  }

  const hasMatches = scan.results?.results?.length > 0;
  if (hasMatches) {
    return { icon: AlertCircle, className: 'w-3 h-3 mr-1.5 text-red-500' };
  }

  return { icon: Shield, className: 'w-3 h-3 mr-1.5 text-green-500' };
}

interface ImageBoxProps {
  src?: string;
  alt?: string;
  imageId?: string;
  className?: string;
  isAttachment?: boolean;
  reversed?: boolean;
  instagramCaption?: string;
}

function ImageBox({
  src = '',
  alt,
  imageId,
  className = '',
  isAttachment = false,
  reversed = false,
  instagramCaption,
}: ImageBoxProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const { amplitude, intercom } = useAnalytics();
  const { user, profile: userProfile } = useAuth();
  const { images } = useImages();
  const { mode } = useChatUI();
  const isPublicView = mode === 'public';
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [mocksOpen, setMocksOpen] = useState(false);
  const [artGuardOpen, setArtGuardOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareTitle, setShareTitle] = useState('');
  const [shareQuote, setShareQuote] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const detection = detectUserImage(src, user?.user_id, isAttachment ? imageId : undefined);
  const resolvedImageId = (imageId ? String(imageId) : undefined) ?? detection.detectedImageId;
  const contextImage = resolvedImageId ? images.find((img) => img.id.toString() === resolvedImageId) : null;
  const shouldFetchImage = Boolean(
    resolvedImageId && !contextImage && !isPublicView && user?.user_id && (detection.isUserImage || Boolean(imageId))
  );
  const { data: imageQueryResult, loading: imageQueryLoading } = useQuery(IMAGE_QUERY, {
    variables: { id: resolvedImageId! },
    skip: !shouldFetchImage,
    fetchPolicy: 'cache-first',
  });

  const imageData = contextImage ?? imageQueryResult?.image ?? null;
  const gallerySrc = imageData?.optimizedUrl || imageData?.thumbnailUrl;
  const displaySrc = gallerySrc || src || '';
  const isOwnerImage =
    !isPublicView &&
    Boolean(resolvedImageId) &&
    (Boolean(contextImage) || detection.isUserImage || Boolean(imageQueryResult?.image));

  // Query scan status for button styling
  const { data: scanData } = useQuery(LATEST_COPYRIGHT_SCAN, {
    variables: { imageId: resolvedImageId || '' },
    skip: !resolvedImageId || !isOwnerImage,
    fetchPolicy: 'cache-first',
  });

  // Display-only branch is for assistant markdown images — never for chat attachments
  if (!isOwnerImage && !isAttachment) {
    if (!displaySrc) {
      if (shouldFetchImage && imageQueryLoading) {
        return (
          <div
            className={`flex items-center justify-center bg-gray-50 rounded-lg my-4 ${className}`}
            style={{ width: '200px', height: '200px' }}
          >
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        );
      }

      return null;
    }

    return (
      <div
        className={`group relative inline-block max-w-sm my-4 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative overflow-hidden rounded-lg">
          <div className="relative overflow-hidden rounded ">
            <img
              src={displaySrc}
              alt={alt || t('image-box.artwork-alt')}
              className={`w-full h-auto object-contain transition-transform duration-300 ${
                isHovered ? 'scale-105' : 'scale-100'
              }`}
              loading="lazy"
              style={{ maxHeight: '250px' }}
            />

            {/* Zoom overlay on hover */}
            {isHovered && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center transition-opacity duration-300">
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-white/90 backdrop-blur-sm hover:bg-white text-xs"
                  onClick={() => setIsExpanded(true)}
                >
                  <ZoomInIcon className="w-3 h-3 mr-1" />
                  {tc('view')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Full size modal */}
        {isExpanded &&
          createPortal(
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setIsExpanded(false)}
            >
              <div className="relative max-w-4xl max-h-full">
                <img
                  src={displaySrc}
                  alt={alt}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  onClick={() => setIsExpanded(false)}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }

  // User image with action buttons
  const handleEditDetailsClick = () => {
    amplitude.track('chat_imagebox_edit_details_clicked');
    intercom.trackEvent('chat_imagebox_edit_details_clicked');
    setEditDetailsOpen(true);
  };

  const handleRoomMockupsClick = () => {
    amplitude.track('chat_imagebox_room_mockups_clicked');
    intercom.trackEvent('chat_imagebox_room_mockups_clicked');
    setMocksOpen(true);
  };

  const handlePostClick = () => {
    amplitude.track('chat_imagebox_new_post_clicked');
    intercom.trackEvent('chat_imagebox_new_post_clicked');

    const completion: Completion = {
      id: '',
      content: instagramCaption || '',
      image: imageData!,
      responseHtml: instagramCaption || '',
    };

    navigate('/post', {
      state: {
        completion: completion,
        initialStep: 1,
        initialAspectRatio: {
          id: '1:1',
          icon: 'square',
          label: 'Square (1:1)',
          mobileLabel: '1:1',
          value: 1,
        },
        from: window.location.pathname,
      },
    });
  };

  const handleArtGuardClick = () => {
    // Keep legacy event for backward compatibility during transition
    amplitude.track('chat_imagebox_artguard_clicked');
    intercom.trackEvent('chat_imagebox_artguard_clicked');
    setArtGuardOpen(true);
  };

  const handleViewClick = () => {
    if (userProfile?.profile?.slug && imageData?.slug) {
      const publicImageUrl = getPublicImageUrl(userProfile.profile.slug, imageData.slug);
      window.open(publicImageUrl, '_blank', 'noopener,noreferrer');

      amplitude.track('chat_imagebox_view_clicked');
      intercom.trackEvent('chat_imagebox_view_clicked');
    }
  };

  const handleShareClick = () => {
    if (userProfile?.profile?.slug && imageData?.slug) {
      const publicImageUrl = getPublicImageUrl(userProfile.profile.slug, imageData.slug);
      const urlWithUtm = `${publicImageUrl}?utm_source=arthelper&utm_term=chat_image_box_share_link`;

      setShareUrl(urlWithUtm);
      setShareTitle(imageData?.title || t('image-box.share-fallback'));
      setShareQuote(imageData?.title || t('image-box.share-fallback'));
      setShowShareModal(true);

      amplitude.track('new_post_clicked', {
        location: 'chat_imagebox',
      });
      intercom.trackEvent('new_post_clicked', {
        location: 'chat_imagebox',
      });
    }
  };

  // Disable action buttons until gallery metadata is available; still show the uploaded image
  const isMetadataLoading = !!resolvedImageId && !imageData && (imageQueryLoading || isAttachment);
  const actionsDisabled = !!resolvedImageId && !imageData;

  if ((isOwnerImage || isAttachment) && !displaySrc) {
    if (isMetadataLoading) {
      return (
        <div className={`my-4 flex max-w-xs items-center justify-center rounded bg-gray-100 p-6 ${className}`}>
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        </div>
      );
    }

    return (
      <div
        className={`my-4 max-w-xs rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500 ${className}`}
      >
        {alt || imageData?.title || t('image-box.artwork-alt')}
      </div>
    );
  }

  return (
    <>
      <div
        className={`group relative max-w-xs my-4 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Flex container for image and buttons */}
        <div className={`flex gap-3 items-center ${reversed ? 'flex-row-reverse sm:flex-row-reverse' : ''}`}>
          {/* Image container */}
          <div className="relative overflow-hidden rounded flex-shrink-0">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              </div>
            )}
            <img
              src={displaySrc}
              alt={alt || imageData?.title || t('image-box.processing-alt')}
              className={`w-full h-auto object-contain transition-opacity duration-300 cursor-pointer ${
                isLoading && !isAttachment ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              onClick={() => {
                if (resolvedImageId) {
                  navigate(`/art/${resolvedImageId}`);
                }
              }}
              style={{ width: '140px', maxHeight: '140px' }}
            />

            {/* Subtle metadata loading hint — image remains visible underneath */}
            {isMetadataLoading && (
              <div className="absolute bottom-1 right-1 rounded bg-white/90 px-1.5 py-0.5 shadow-sm">
                <div className="flex items-center gap-1">
                  <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                  <p className="text-[10px] text-gray-600">{t('image-box.processing')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Right side content */}
          <div className="flex-1 min-w-0">
            {/* Title only (no alt text) */}
            {imageData?.title && (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-800 leading-tight line-clamp-2">{imageData.title}</h4>
              </div>
            )}

            {/* Action buttons - stacked vertically, most important first */}
            <div className="flex flex-col gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={actionsDisabled ? undefined : handleShareClick}
                disabled={actionsDisabled}
                className="text-xs h-7 justify-start"
              >
                <SendIcon className="w-3 h-3 mr-1.5" />
                {t('image-box.post-to-social')}
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={actionsDisabled ? undefined : handleRoomMockupsClick}
                disabled={actionsDisabled}
                className="text-xs h-7 justify-start"
              >
                <ImageIcon className="w-3 h-3 mr-1.5" />
                {t('image-box.room-mockups')}
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={actionsDisabled ? undefined : handleEditDetailsClick}
                disabled={actionsDisabled}
                className="text-xs h-7 justify-start"
              >
                <PencilIcon className="w-3 h-3 mr-1.5" />
                {t('image-box.edit-details')}
              </Button>

              {__COPYRIGHT_SCANNING_ENABLED__ && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={actionsDisabled ? undefined : handleArtGuardClick}
                  disabled={actionsDisabled}
                  className="text-xs h-7 justify-start"
                >
                  {(() => {
                    const { icon: IconComponent, className } = getArtGuardIcon(scanData);
                    return <IconComponent className={className} />;
                  })()}
                  ArtGuard
                </Button>
              )}

              {/* View button */}
              <Button
                size="sm"
                variant="outline"
                onClick={actionsDisabled ? undefined : handleViewClick}
                disabled={actionsDisabled || !imageData?.isPublic || !userProfile?.profile?.slug || !imageData?.slug}
                className="text-xs h-7 justify-start"
              >
                <ExternalLink className="w-3 h-3 mr-1.5" /> {tc('view')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Render modals in portal to avoid DOM nesting issues */}
      {editDetailsOpen &&
        imageData &&
        createPortal(
          <ArtworkDetailsForm
            image={imageData}
            open={editDetailsOpen}
            onOpenChange={setEditDetailsOpen}
            type="sheet"
            contentType="image"
          />,
          document.body
        )}

      {mocksOpen &&
        imageData &&
        user &&
        resolvedImageId &&
        createPortal(
          <Mocks
            imageId={parseInt(resolvedImageId)}
            open={mocksOpen}
            setOpen={setMocksOpen}
            imageDimensions={{
              imageWidth: imageData.width || 0,
              imageHeight: imageData.height || 0,
              imageDimensionType: imageData.dimensionType || 'inches',
            }}
            imageOriginalWidth={imageData.originalWidth}
            imageOriginalHeight={imageData.originalHeight}
          />,
          document.body
        )}

      {artGuardOpen &&
        imageData &&
        resolvedImageId &&
        createPortal(
          <ArtGuardModal
            imageId={resolvedImageId}
            imageTitle={imageData.title}
            open={artGuardOpen}
            onOpenChange={setArtGuardOpen}
            source="chat_imagebox"
          />,
          document.body
        )}

      <ShareModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareUrl('');
          setShareTitle('');
          setShareQuote('');
        }}
        url={shareUrl}
        title={shareTitle}
        quote={shareQuote}
        showPostToInstagram={true}
        onPostToInstagram={handlePostClick}
      />
    </>
  );
}

export default React.memo(ImageBox);
